import { useState } from "react";
import type { Card as CardType } from "@stackje/common/config";
import { UnsavedChangesDialog } from "../shared/UnsavedChangesDialog";
import { BottomBar } from "../shared/BottomBar";
import { Button } from "@stackje/shared-components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Loader2, RocketIcon } from "lucide-react";
import { TopBar } from "../shared/TopBar";
import { ManageCards } from "./ManageCards";
import { SitesRepository } from "@/repositories/SitesRepository";
import type { Site } from "@/types/site.types";
import { useToast } from "@stackje/shared-components/hooks/useToast";
import { captureErrorAndToast } from "@/utils/error-utils";
import { useInvalidateSiteCache } from "@/hooks/useSiteQuery";

export function ManageCardsPage({
  cards,
  site,
}: {
  cards: CardType[];
  site: Site;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const invalidateSiteCache = useInvalidateSiteCache();
  const [pendingChanges, setPendingChanges] = useState<CardType[] | null>(null);
  const [publishState, setPublishState] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handlePublish = async () => {
    setPublishState("loading");
    if (!pendingChanges || pendingChanges.length === 0) {
      setPublishState("idle");
      return;
    }

    try {
      const page = site.config?.pages?.[0];
      if (!page) {
        throw new Error("Page 0 not found");
      }

      page.cards = pendingChanges;

      const { error } = await SitesRepository.update(site.id, site.userId, {
        config: {
          ...site.config,
        },
      });

      if (error) {
        throw error;
      }

      invalidateSiteCache(site.userId);

      setPublishState("success");
      setPendingChanges(null);
      toast({
        title: "Success",
        description: "Cards order updated successfully",
      });
    } catch (e) {
      captureErrorAndToast(e, toast, {
        description: `Failed to update cards order: ${e}`
      });
    } finally {
      setPublishState("idle");
    }
  };

  const handleBackClick = () => {
    navigate({ to: "/" });
  };

  return (
    <div className="mx-auto">
      <TopBar>
        <Button variant="ghost" size="icon" onClick={handleBackClick}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h1 className="">Manage Cards</h1>
        <div className="w-4"></div>
      </TopBar>

      <div className="container mx-auto p-4 mt-20">
        <ManageCards
          cards={cards}
          onReorder={setPendingChanges}
          isPendingChanges={!!pendingChanges}
        />
      </div>

      {pendingChanges && (
        <BottomBar>
          <div></div>
          <Button
            onClick={handlePublish}
            className="text-white px-12"
            disabled={publishState !== "idle"}
          >
            {publishState !== "idle" ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <RocketIcon className="h-5 w-5 mr-2" />
                Save Order
              </>
            )}
          </Button>
          <div></div>
        </BottomBar>
      )}

      <UnsavedChangesDialog isDirty={!!pendingChanges} />
    </div>
  );
}
